import React from 'react';
import { Box } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { classes } from './styles';
import moment from 'moment';
import QuestionFieldViewer from '../../Components/Utilities/QuestionFieldViewer';
import Attachments from './Attachment/Attachments';
import { createJobDefault } from '../config';
import { IGeneratedContent } from 'app/javascript/components/AIStudio/NewJobIntegration/AIStudioNewJob';
import { IJob } from '../../Job/types';
import { QuestionsPreview } from '../../../AIStudio/NewJobIntegration/QuestionsPreview';
import DisplayHTMLCollapsible from '../../../AIStudio/SharedComponents/DisplayHTMLCollapsible';
import { IApplicantQuestion } from '../../Components/Utilities/QuestionFieldViewer';

export default function NewJobStep3({
  newJobData,
  showEntitySection,
  clonedJob,
  selectedEntity,
  aiStudioEnabled,
  createType,
  generatedContent,
  userTimezone,
  tlpPreviewCollapsed,
  setTlpPreviewCollapsed,
  resources,
  copiedJob
}: {
  newJobData: typeof createJobDefault;
  showEntitySection: boolean;
  clonedJob: string | null;
  selectedEntity: { id: number; name: string } | null;
  aiStudioEnabled: boolean;
  createType: string;
  generatedContent: IGeneratedContent;
  userTimezone: string;
  tlpPreviewCollapsed: boolean;
  setTlpPreviewCollapsed: (collapsed: boolean) => void;
  resources: Record<string, boolean>;
  copiedJob: IJob | null;
}) {
  return (
    <Box
      sx={classes.modalContentStep3}
      id="new-job-modal-content"
      aria-label="New job modal content"
    >
      <Box
        id="new-job-modal-title"
        data-testid="new-job-modal-title-step-3"
        sx={classes.modalHeaderContent}
      >
        Final step! Is this correct?
      </Box>
      <Box role="form" id="new-job-form" aria-label="New job form">
        <Box sx={{ ...classes.modalFormLine, display: 'contents' }}>
          <Box sx={classes.reviewSubheaders}>Reference</Box>
          <Box sx={classes.reviewContent}>{newJobData.reference.value}</Box>
          <Box sx={classes.reviewSubheaders}>Job Title</Box>
          <Box sx={classes.reviewContent}>{newJobData.title.value}</Box>
          {showEntitySection && !clonedJob && (
            <>
              <Box sx={classes.reviewSubheaders}>Entity</Box>
              <Box sx={classes.reviewContent}>{selectedEntity?.name}</Box>
            </>
          )}
        </Box>
        <Box sx={classes.modalFormLine}>
          <Box sx={classes.reviewBoxes}>
            <Box>City/town</Box>
            <Box sx={{ fontWeight: '800' }}>{newJobData.location.value}</Box>
          </Box>
          <Box sx={classes.reviewBoxes}>
            <Box>Expiry</Box>
            <Box sx={{ fontWeight: '800' }}>
              {moment(newJobData.expires_at.value)
                .tz(userTimezone ?? moment.tz.guess())
                .format('D MMM YYYY, hh:mm a')}
            </Box>
          </Box>
        </Box>
        {aiStudioEnabled &&
          createType !== 'Clone existing job' &&
          generatedContent.talentLandingPage && (
            <Box sx={classes.previewContainer}>
              <h3>Talent landing page preview</h3>
              <DisplayHTMLCollapsible
                content={generatedContent.talentLandingPage}
                collapsed={tlpPreviewCollapsed}
                setCollapsed={setTlpPreviewCollapsed}
              />
            </Box>
          )}
        {aiStudioEnabled &&
          createType !== 'Clone existing job' &&
          generatedContent.screeningQuestions.length > 0 && (
            <QuestionsPreview questions={generatedContent.screeningQuestions} previewOnly={true} />
          )}
        <Box>
          {createType === 'Clone existing job' && copiedJob ? (
            <Box>
              {resources['resources[description]'] ? (
                <Box sx={{ ...classes.modalFormLine, display: 'contents' }}>
                  <Box sx={classes.reviewSubheaders}>Description</Box>
                  {copiedJob?.description ? (
                    <Box sx={classes.reviewContent}>
                      <div dangerouslySetInnerHTML={{ __html: copiedJob?.description }} />
                    </Box>
                  ) : (
                    <Box>No Description</Box>
                  )}
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[questions]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '1.5rem'
                  }}
                >
                  <Box sx={{ ...classes.reviewSubheaders, marginBottom: '0.5rem' }}>Questions</Box>
                  {copiedJob?.questions && copiedJob?.questions.length > 0 ? (
                    <Box>
                      {copiedJob?.questions &&
                        copiedJob.questions.map((question) => (
                          <Box key={question.id}>
                            {question.type === 'AvailabilityField' ||
                            question.type === 'CsvDataLookupField' ||
                            question.type === 'JobReferenceLookupField' ? (
                              <span />
                            ) : (
                              <Box sx={classes.questionsWrapper}>
                                <Box sx={{ marginRight: 'auto', ...classes.questionInput }}>
                                  <Box sx={classes.questionsTitle}>Title</Box>
                                  <Box
                                    sx={{
                                      ...classes.modalFormLine,
                                      position: 'relative'
                                    }}
                                  >
                                    <QuestionFieldViewer
                                      question={question as IApplicantQuestion}
                                    />
                                  </Box>
                                </Box>
                                <Box sx={{ padding: '0 6px' }}>
                                  <Box sx={classes.questionsTitle}>Enabled</Box>
                                  {question.enabled ? (
                                    <CheckCircleIcon sx={classes.iconGreen} />
                                  ) : (
                                    <CancelIcon sx={classes.iconRed} />
                                  )}
                                </Box>
                                <Box sx={{ padding: '0 6px' }}>
                                  <Box sx={classes.questionsTitle}>Required</Box>
                                  {question.required ? (
                                    <CheckCircleIcon sx={classes.iconGreen} />
                                  ) : (
                                    <CancelIcon sx={classes.iconRed} />
                                  )}
                                </Box>
                                <Box sx={{ padding: '0 6px' }}>
                                  <Box sx={classes.questionsTitle}>Rateable</Box>
                                  {question.rateable ? (
                                    <CheckCircleIcon sx={classes.iconGreen} />
                                  ) : (
                                    <CancelIcon sx={classes.iconRed} />
                                  )}
                                </Box>
                                <Box sx={{ padding: '0 6px' }}>
                                  <Box sx={classes.questionsTitle}>Confidential</Box>
                                  {question.confidential ? (
                                    <CheckCircleIcon sx={classes.iconGreen} />
                                  ) : (
                                    <CancelIcon sx={classes.iconRed} />
                                  )}
                                </Box>
                                <Box sx={{ paddingLeft: '6px' }}>
                                  <Box sx={classes.questionsTitle}>Weight</Box>
                                  <Box sx={classes.weightValue}>{question.weight}</Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box sx={{ paddingLeft: '4px' }}>No Questions</Box>
                  )}
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[attachments]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '1.5rem'
                  }}
                >
                  <Box sx={classes.reviewSubheaders}>Attachments</Box>
                  <Attachments jobId={copiedJob?.id} />
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[sources]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '2rem'
                  }}
                >
                  <Box sx={classes.reviewSubheaders}>Sources</Box>
                  <Box
                    sx={{
                      ...classes.reviewContent,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Imported <CheckCircleIcon sx={classes.iconGreenResources} />
                  </Box>
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[auto_actions]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '2rem'
                  }}
                >
                  <Box sx={classes.reviewSubheaders}>Auto-Actions</Box>
                  <Box
                    sx={{
                      ...classes.reviewContent,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Imported <CheckCircleIcon sx={classes.iconGreenResources} />
                  </Box>
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[options]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '2rem'
                  }}
                >
                  <Box sx={classes.reviewSubheaders}>Options</Box>
                  <Box
                    sx={{
                      ...classes.reviewContent,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Imported <CheckCircleIcon sx={classes.iconGreenResources} />
                  </Box>
                </Box>
              ) : (
                <Box />
              )}
              {resources['resources[visibility]'] ? (
                <Box
                  sx={{
                    ...classes.modalFormLine,
                    display: 'contents',
                    marginTop: '2rem'
                  }}
                >
                  <Box sx={classes.reviewSubheaders}>Visibility</Box>
                  <Box
                    sx={{
                      ...classes.reviewContent,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Imported <CheckCircleIcon sx={classes.iconGreenResources} />
                  </Box>
                </Box>
              ) : (
                <Box />
              )}
            </Box>
          ) : (
            <span />
          )}
        </Box>
      </Box>
    </Box>
  );
}
